import { inject, Injectable } from '@angular/core';
import { WidgetTabs } from '@com-delta-idp/mnx-sitewide-header-ui';
import { BellyBand } from '@com-delta-idp/mnx-sitewide-shared-core-smart';
import { EXTERNAL_LINK_ARIA_LABEL } from '@com-delta-idp/mnx-sitewide-shared-utils';
import { TabItem, TabsOptions } from '@com-delta-omnichannel/mach-core-js';
import { HeaderStateService } from '../../../entry-components/header/header-base/services';
import { ScreenDetectionService } from '../../../entry-components/header/header-base/services/screen-detection.service';
import * as CONSTANTS from '../../constants/booking-tab/booking-tab.constants';
import { BookingTabTexts } from '../../models/interface/booking-tab/booking-tab.interfaces';

@Injectable({ providedIn: 'root' })
export class BookingTabAdapterService {
  private headerStateService = inject(HeaderStateService);
  private screenDetection = inject(ScreenDetectionService);
  private initializedForAdvanceSearch = false;

  getContent(aem: BellyBand, index: number): WidgetTabs {
    return {
      tabBookingOptions: this.getTabBookingOptions(aem, index),
      externalLinks: {
        text: this.getExternalLinksText(aem),
        links: this.getExternalLinksUrls(aem),
        accessibility: {
          altText:
            aem?.externalLinks?.accessibility?.altText || CONSTANTS.ALT_TEXT
        }
      }
    };
  }

  private getTabBookingOptions(aem: BellyBand, index: number): TabsOptions {
    return {
      id: aem?.bookingTab ? aem.id : CONSTANTS.BOOKING_ID_TAB,
      tabVariant: CONSTANTS.BOOKING,
      tabItems: this.createTabItems(aem?.bookingTab),
      selectedIndex: index
    };
  }

  private getExternalLinksText(aem: BellyBand) {
    const text = aem?.externalLinks?.text;
    return {
      deltaAmexCard: text?.deltaAmexCard || CONSTANTS.DELTA_AMEX_CARD,
      giftCards: text?.giftCards || CONSTANTS.GIFT_CARDS,
      updateBagTravelFees:
        text?.updateBagTravelFees || CONSTANTS.UPDATE_BAGGAGE_TRAVEL_FEES
    };
  }

  private getExternalLinksUrls(aem: BellyBand) {
    const links = aem?.externalLinks?.links;
    return {
      ...this.getAmexAndGiftCardLinks(links),
      ...this.getBaggageAndCruiseLinks(links)
    };
  }

  private getAmexAndGiftCardLinks(
    links: BellyBand['externalLinks']['links'] | undefined
  ) {
    return {
      deltaAmexCard: links?.deltaAmexCard || CONSTANTS.DELTA_AMEX_CARD_LINK,
      giftCard: links?.giftCard || CONSTANTS.GIFT_CARD_LINK
    };
  }

  private getBaggageAndCruiseLinks(
    links: BellyBand['externalLinks']['links'] | undefined
  ) {
    return {
      updateBagTravelFees:
        links?.updateBagTravelFees || CONSTANTS.UPDATE_BAGGAGE_TRAVEL_FEES_LINK,
      cruises: links?.cruises || CONSTANTS.CRUISES_LINK
    };
  }

  private createTabItems(bookingTab: BookingTabTexts | undefined): TabItem[] {
    const items = this.getBaseTabItems(bookingTab);
    return items;
  }

  private getBaseTabItems(bookingTab: BookingTabTexts | undefined): TabItem[] {
    return [
      this.createTabItem(bookingTab?.flightsText, CONSTANTS.FLIGHTS),
      this.createTabItem(bookingTab?.staysText, CONSTANTS.STAYS),
      this.createTabItem(bookingTab?.carsText, CONSTANTS.CARS),
      this.createTabItem(bookingTab?.vacationsText, CONSTANTS.VACATIONS),
      this.createTabItem(bookingTab?.cruisesText, CONSTANTS.CRUISES, true)
    ];
  }

  private createTabItem(
    text: string | undefined,
    fallback: string,
    externalLink = false
  ): TabItem {
    return {
      id: fallback.toLowerCase(),
      label: text || fallback,
      value: text || fallback,
      selected: false,
      ariaLabel: externalLink ? `${text} ${EXTERNAL_LINK_ARIA_LABEL}` : ''
    };
  }
}
